/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useMemo, useState } from 'react';
import { Button, DynamicForm, ToggleSwitch, Checkbox } from 'components';
import useFormHandler from 'hooks/useFormHandler';
import useApi from 'hooks/useApi';
import { changeTimezoneAndConvertToUTC } from 'helpers/date';
import { Props } from 'types/modals';
import Base from '../../base';
import schema from './schema';
import endpoints from 'constants/endpoints';

const CreationEventModal = ({ closing, onClose }: Props) => {
  const formHandler = useFormHandler(schema);
  const [referralBonusEligible, setReferralBonusEligible] = useState(false);
  const api = useApi();

  const handleSubmit = async () => {
    formHandler.setIsSubmitting(true);
    const { data } = formHandler;
    const { id, timezone } = data.venue;

    const formData = new FormData();

    formData.append('venueId', id ?? '');
    formData.append(
      'allowRegistrationsAt',
      changeTimezoneAndConvertToUTC(data.allowRegistrationsAt, timezone)?.toISO() ?? ''
    );
    formData.append('heldOn', changeTimezoneAndConvertToUTC(data.heldOn, timezone)?.toISO() ?? '');
    if (data.heldOnTimeIsUnknown !== undefined) {
      formData.append('heldOnTimeIsUnknown', String(data.heldOnTimeIsUnknown));
    }
    // required by schema; safe to append
    formData.append('eventType', data.eventType);
    if (data.reportTimeAt)
      formData.append('reportTimeAt', changeTimezoneAndConvertToUTC(data.reportTimeAt, timezone)?.toISO() ?? '');
    if (data.reportTimeIsUnknown !== undefined) {
      formData.append('reportTimeIsUnknown', String(data.reportTimeIsUnknown));
    }
    if (data.employeeCapacity) formData.append('employeeCapacity', String(data.employeeCapacity));
    if (data.buffer) formData.append('buffer', String(data.buffer));
    if (data.automateRoster !== undefined) formData.append('automateRoster', String(data.automateRoster));
    formData.append('referralBonusActive', String(referralBonusEligible));
    if (data.templateId) formData.append('templateId', String(data.templateId));
    if (data.eventTrainingUrl) formData.append('eventTrainingUrl', data.eventTrainingUrl ?? '');
    if (data.hireflixUrl) formData.append('hireflixUrl', data.hireflixUrl ?? '');
    if (data.groupMeUrl) formData.append('groupMeUrl', data.groupMeUrl ?? '');
    if (data.name) formData.append('name', data.name ?? '');
    if (data.tags && Array.isArray(data.tags)) formData.append('tags', JSON.stringify(data.tags));

    if (data.payModelFile) {
      formData.append('payModelPdf', data.payModelFile);
    }
    if (data.eventLogisticsFile) {
      formData.append('eventLogisticPdf', data.eventLogisticsFile, data.eventLogisticsFile.name);
    }

    await api.fetchData(endpoints.CREATE_EVENT.get(), formData);

    onClose({ created: true });
  };

  useEffect(() => {
    const venueId = formHandler.data.venue.id;

    const getTemplates = async () => {
      const resp = api.fetchData(endpoints.SEARCH_TEMPLATES.get({ venueId }));
      return resp;
    };

    if (venueId) {
      getTemplates().then(res => {
        const data = { ...formHandler.data, templateId: '' };
        const newSchema = {
          ...formHandler.schema,
          templateId: { ...formHandler.schema.templateId, options: res.items }
        };
        formHandler.setFullSchema(newSchema, data);
      });
    }
  }, [formHandler.data.venue.id]);

  const handlerWithoutAutomateRoster = useMemo(() => {
    const { automateRoster, ...rest } = formHandler.schema as any;
    return { ...formHandler, schema: rest };
  }, [formHandler]);

  return (
    <Base
      header="Create event"
      closing={closing}
      onClose={onClose}
      size="md"
      className="modal-create-event-base"
      footer={
        <div className="create-event-footer">
          <Button className="cancel-button-create-event" text="Cancel" variant="secondary" onClick={() => onClose()} />
          <Button
            className="create-button-create-event"
            text="Create"
            disabled={!formHandler.valid || formHandler.isSubmitting}
            onClick={handleSubmit}
          />
        </div>
      }
    >
      <div className="modal-create-event">
        <DynamicForm handler={handlerWithoutAutomateRoster} />

        <div className="referral-bonus-toggle">
          <ToggleSwitch value={referralBonusEligible} onChange={setReferralBonusEligible} />
          <span className="referral-bonus-label">Is this event eligible for referral bonus?</span>
        </div>

        <div className="input-wrapper">
          <Checkbox
            label={formHandler.schema.automateRoster.label}
            checked={formHandler.data.automateRoster}
            onChange={e => formHandler.setFormData('automateRoster')(e.target.checked)}
          />
        </div>
      </div>
    </Base>
  );
};

export default CreationEventModal;
